import { ICellRendererParams, ValueFormatterParams } from 'ag-grid-enterprise'
import { CircleQuestionMark } from 'lucide-react'

import { numberFormatter } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { Tooltip, TooltipContent, TooltipTrigger } from '@fortem-monorepo/react-ui'
import { ICReport } from '@fortem-monorepo/web-sdk'

import { ICReportTablePrimitive } from '@/pages/funds/ic-report/components/primitives/types'
import { labels } from '@/pages/funds/ic-report/constants'
import { fundLevel } from '@/react-query/queries/ic-report/hooks'

const localValueFormatter = (params: ValueFormatterParams) => {
  return [
    'Return / Volatility',
    'Skew',
    'Benchmark Correlation',
    'Benchmark Beta',
    'BCOM beta',
  ].includes(params?.data?.index)
    ? numberFormatter({ delimiter: true })(params)
    : numberFormatter({ suffix: '%', transformer: (val) => val * 100 })(params)
}

const colDefs = createColDefs([
  {
    field: 'index',
    headerName: 'Realised',
    cellRenderer: (params: ICellRendererParams<ICReport.GetFundLevelMetricsData>) => {
      const tooltipText =
        labels.fund_level.fund_performance_metrics().descriptions[
          params.value as keyof typeof labels.fund_level.fund_performance_metrics
        ]

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={'flex items-center gap-2 flex-1 min-w-full!'}>
              <CircleQuestionMark size={16} className={'text-blue-600 shrink-0'} />
              {params.value}
            </div>
          </TooltipTrigger>
          {tooltipText && (
            <TooltipContent align={'start'} className={'px-4 py-2 text-xs max-w-100'}>
              {tooltipText}
            </TooltipContent>
          )}
        </Tooltip>
      )
    },
  },
  {
    field: '1_month',
    headerName: '1m',
    valueFormatter: localValueFormatter,
    cellStyle: {
      textAlign: 'center',
    },
  },
  {
    field: '3_month',
    headerName: '3m',
    valueFormatter: localValueFormatter,
    cellStyle: {
      textAlign: 'center',
    },
  },
  {
    field: '1_year',
    headerName: '1y',
    valueFormatter: localValueFormatter,
    cellStyle: {
      textAlign: 'center',
    },
  },
  {
    field: '3_year',
    headerName: '3y',
    valueFormatter: localValueFormatter,
    cellStyle: {
      textAlign: 'center',
    },
  },
  {
    field: '5_year',
    headerName: '5y',
    valueFormatter: localValueFormatter,
    cellStyle: {
      textAlign: 'center',
    },
  },
  {
    field: 'calendar_ytd',
    headerName: 'YTD',
    valueFormatter: localValueFormatter,
  },
  {
    field: 'since_inception',
    headerName: 'Inception',
    valueFormatter: localValueFormatter,
  },
])

export interface FundLevelMetricsTableProps extends ICReportTablePrimitive {}

export const FundLevelMetricsTable: React.FC<FundLevelMetricsTableProps> = ({
  params,
  options,
}) => {
  const query = fundLevel.useMetrics(params)
  const data = (query.data ?? []) as any[]

  return <CustomAgGrid rowData={data} columnDefs={colDefs} {...options} />
}
